import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUpdatePasswordMutation } from '../../app/services/users';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import Visibility from '@mui/icons-material/Visibility';
import { SettingsCard, SettingsCardHeader, SettingsCardContent } from '../../components/SettingsCard';
import { Box, IconButton, TextField } from '@mui/material';

import { validateFormData, getValidationErrorMessage } from '../../helpers';
import { postNewPasswordSchema } from '../../schemas';
import { commonTextFieldStylesWithDisabled } from '../../utils/textFieldStyles';

function AccountSecurity({ userId, provider, providerChanged }) {
  const { t } = useTranslation();

  const getFields = (t) => [
    {
      name: 'oldPassword',
      label: t('generic.oldPasswordFieldLabel'),
    },
    {
      name: 'newPassword',
      label: t('generic.newPasswordFieldLabel'),
    },
  ];

  const initialValue = { oldPassword: '', newPassword: '' };
  const [value, setValue] = useState(initialValue);
  const handleChange = (e) => {
    setValue((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  // Check if form has changes
  const isDirty = value.oldPassword !== '' || value.newPassword !== '';

  const [passwordVisible, setPasswordVisible] = useState({ oldPassword: '', newPassword: '' });
  const handleChangeVisibility = (fieldName) => {
    setPasswordVisible((prev) => ({ ...prev, [fieldName]: !prev[fieldName] }));
  };

  const [formValidationErrors, setFormValidationErrors] = useState(null);
  const oldPasswordError = getValidationErrorMessage(formValidationErrors, 'oldPassword');
  const newPasswordError = getValidationErrorMessage(formValidationErrors, 'newPassword');

  const [updatePassword] = useUpdatePasswordMutation();

  const handleSubmit = async (e) => {
    if (e) {
      e.preventDefault();
    }
    if (!userId) return;

    const validationErrors = validateFormData(t, postNewPasswordSchema, value);
    if (!!validationErrors) {
      setFormValidationErrors(validationErrors);
      return;
    }

    setFormValidationErrors(null);

    try {
      await updatePassword({
        id: userId,
        passwords: { currentPassword: value.oldPassword, newPassword: value.newPassword },
      });

      setValue(initialValue);
      setFormValidationErrors(null);
    } catch (error) {
      console.error(error);
    }
  };

  const handleCancel = () => {
    setValue(initialValue);
    setFormValidationErrors(null);
  };

  // Determine subtitle based on provider
  const getSubtitle = () => {
    if (provider === 'microsoft') {
      return providerChanged
        ? t('userProfile.securitySubtitleMicrosoftChanged')
        : t('userProfile.securitySubtitleMicrosoft');
    }
    return t('userProfile.securitySubtitle');
  };

  return (
    <SettingsCard>
      <SettingsCardHeader
        title={t('generic.passwordUpdateTitle')}
        subtitle={getSubtitle()}
        tooltipText={t('userProfile.information')}
        alwaysEditable={provider !== 'microsoft'}
        isDirty={isDirty}
        onSave={handleSubmit}
        onCancel={handleCancel}
      />
      
      <SettingsCardContent>
        <form onSubmit={handleSubmit}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: '20px',
            }}
          >
            {getFields(t).map((field) => (
              <TextField
                key={field.name}
                label={field.label}
                variant="outlined"
                name={field.name}
                type={passwordVisible[field.name] ? 'text' : 'password'}
                value={value[field.name]}
                onChange={handleChange}
                fullWidth
                sx={commonTextFieldStylesWithDisabled}
                disabled={provider === 'microsoft'}
                InputProps={{
                  endAdornment: provider === 'microsoft' ? null : (
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => {
                        handleChangeVisibility(field.name);
                      }}
                      edge="end"
                    >
                      {passwordVisible[field.name] ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  ),
                }}
                InputLabelProps={{
                  shrink: true,
                }}
                error={field.name === 'oldPassword' ? !!oldPasswordError : !!newPasswordError}
                helperText={
                  field.name === 'oldPassword' ? oldPasswordError ?? ' ' : newPasswordError ?? ' '
                }
                placeholder="**********"
              />
            ))}
          </Box>
        </form>
      </SettingsCardContent>
    </SettingsCard>
  );
}

export default AccountSecurity;
